import React, { useState, useEffect, useRef } from 'react';
import { FileDown, FileText, SlidersHorizontal } from 'lucide-react';
import { Agency } from '../../types/agency';
import { formatCurrency } from '../../utils/format';
import { getCompletionColor } from '../../utils/colors';
import AgencyDetailsModal from './AgencyDetailsModal';
import ColumnConfigPanel, { ColumnConfig } from './ColumnConfigPanel';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { downloadStyledExcel, CURRENCY_FMT, PCT_FMT } from '../../utils/excelExport';
import { isPotentialVisible } from '../../utils/period';

const STORAGE_KEY = 'kprod-agence-columns';

const fmtK = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M€`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(0)} k€`;
  return `${v.toFixed(0)} €`;
};

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'position',     label: '#',              visible: true, fixed: true },
  { id: 'name',         label: 'Agence',          visible: true },
  { id: 'company',      label: 'Société',         visible: true },
  { id: 'objective',    label: 'Objectif',        visible: true },
  { id: 'potential',    label: 'Hono Potentiel',  visible: true },
  { id: 'sales',        label: 'Hono Réalisé',    visible: true },
  { id: 'cumulative',   label: 'Hono Cumulé',     visible: true },
  { id: 'remaining',    label: 'Reste',           visible: true },
  { id: 'completion',   label: '% Réalisation',   visible: true },
  { id: 'negotiators',  label: 'Négociateurs',    visible: true },
  { id: 'transactions', label: 'Nb Ventes',       visible: true },
];

function loadColumnsFromStorage(): ColumnConfig[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS.map(c => ({ ...c }));
    const saved: ColumnConfig[] = JSON.parse(raw);
    const defaultMap = new Map(DEFAULT_COLUMNS.map(c => [c.id, c]));
    const merged = saved
      .filter(c => defaultMap.has(c.id))
      .map(c => ({ ...defaultMap.get(c.id)!, visible: c.visible }));
    const savedIds = new Set(saved.map(c => c.id));
    DEFAULT_COLUMNS.filter(c => !savedIds.has(c.id)).forEach(c => merged.push({ ...c }));
    return merged;
  } catch {
    return DEFAULT_COLUMNS.map(c => ({ ...c }));
  }
}

interface TopAgencyTableProps {
  agencies: Agency[];
  startDate?: string;
  endDate?: string;
  period?: 'year' | 'quarter' | 'month';
}

const MEDAL_ICONS = ['🥇', '🥈', '🥉'];
const MEDAL_RINGS = [
  'ring-2 ring-amber-400 ring-offset-1',
  'ring-2 ring-slate-400 ring-offset-1',
  'ring-2 ring-orange-400 ring-offset-1',
];

const COMPANY_GROUPS = [
  { key: 'KI',     label: 'Kermarrec Immobilier', bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    dot: 'bg-blue-700'    },
  { key: 'DRYADE', label: 'Dryade',                bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700',  dot: 'bg-violet-700'  },
  { key: 'KA',     label: 'Kermarrec Atlantique',  bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-700' },
] as const;

export default function TopAgencyTable({ agencies, startDate, endDate, period = 'year' }: TopAgencyTableProps) {
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [classementMode, setClassementMode] = useState<string>('par-entite');
  const [exporting, setExporting] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>(loadColumnsFromStorage);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const columnPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  }, [columns]);

  useEffect(() => {
    if (!showColumnPanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (columnPanelRef.current && !columnPanelRef.current.contains(e.target as Node)) {
        setShowColumnPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnPanel]);

  const hiddenCount = columns.filter(c => !c.visible).length;
  const visibleColumns = columns.filter(c => c.visible);

  const showPotential = endDate ? isPotentialVisible(endDate) : true;

  // ── Helpers ──
  const getObjectiveLabel = () =>
    period === 'year' ? 'Objectif Annuel' : period === 'quarter' ? 'Objectif Trimestriel' : 'Objectif Mensuel';

  const getAgencyInitials = (name: string) => {
    const words = name.trim().split(/\s+/);
    return words.length >= 2
      ? (words[0][0] + words[1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const getAvatarBg = (company: string) => {
    switch (company) {
      case 'KI':     return 'bg-blue-700';
      case 'KA':     return 'bg-emerald-700';
      case 'DRYADE': return 'bg-violet-700';
      default:       return 'bg-slate-700';
    }
  };

  const getCompanyBadgeClass = (company: string) => {
    switch (company) {
      case 'KI':     return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'KA':     return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'DRYADE': return 'bg-violet-50 text-violet-700 border-violet-200';
      default:       return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getCompanyLabel = (company: string) => {
    switch (company) {
      case 'KI':     return 'Kermarrec Immobilier';
      case 'KA':     return 'Kermarrec Atlantique';
      case 'DRYADE': return 'Dryade';
      default:       return company;
    }
  };

  const moneyCell = (value: number, pillClass: string) => {
    if (!value || value === 0) return <span className="text-slate-300 text-sm">—</span>;
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${pillClass}`}>
        {formatCurrency(value)}
      </span>
    );
  };

  // ── Filtrage par entité ──
  const filteredAgencies = (classementMode === 'global' || classementMode === 'par-entite')
    ? agencies
    : agencies.filter(a => a.company === classementMode);

  // ── Exports ──
  const sortedAgencies = [...filteredAgencies].sort((a, b) => b.total_sales - a.total_sales);

  const handleExportExcel = async () => {
    const columns = [
      { header: '#',               key: 'pos',       width: 6,  align: 'center' as const },
      { header: 'Agence',          key: 'name',      width: 28 },
      { header: 'Société',         key: 'company',   width: 14 },
      { header: getObjectiveLabel(), key: 'obj',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Potentiel',  key: 'potential', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Réalisé',    key: 'sales',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Cumulé',     key: 'cumul',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Reste',           key: 'remaining', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: '% Réalisation',   key: 'rate',      width: 14, numFmt: PCT_FMT,      align: 'center' as const },
      { header: 'Négociateurs',    key: 'negs',      width: 14, align: 'center' as const },
      { header: 'Nb Ventes',       key: 'ventes',    width: 10, align: 'center' as const },
    ];

    const rows = sortedAgencies.map((agency, i) => ({
      data: {
        pos:       i + 1,
        name:      agency.name,
        company:   getCompanyLabel(agency.company),
        obj:       agency.annual_objective,
        potential: (agency as any).potential_sales || 0,
        sales:     agency.total_sales,
        cumul:     ((agency as any).potential_sales || 0) + agency.total_sales,
        remaining: agency.remaining,
        rate:      parseFloat(agency.completion_rate.toFixed(1)),
        negs:      agency.negotiator_count,
        ventes:    agency.sales_count,
      },
    }));

    await downloadStyledExcel('top_position_agences.xlsx', 'Par Agence', columns, rows);
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const table = document.querySelector('.agency-table-section');
      if (!table) return;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      pdf.setFontSize(16);
      pdf.text('Classement - Par Agence', 10, 10);
      const canvas = await html2canvas(table as HTMLElement, {
        scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 10, 25, pdfWidth, pdfHeight);
      pdf.save(`top_position_agences.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  // ── Column rendering ──
  const renderHeader = (col: ColumnConfig) => {
    const label = col.id === 'objective' ? getObjectiveLabel() : col.label;
    const base = 'px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider';
    if (col.id === 'position') return <th key="position" className={`${base} w-24`}>#</th>;
    return <th key={col.id} className={base}>{label}</th>;
  };

  const renderCell = (col: ColumnConfig, agency: Agency, index: number) => {
    switch (col.id) {
      case 'position':
        return (
          <td key="position" className="px-4 py-4 whitespace-nowrap">
            <div className="flex items-center gap-1">
              <div className="w-6 text-center">
                {index < 3
                  ? <span className="text-base leading-none">{MEDAL_ICONS[index]}</span>
                  : <span className="text-xs font-bold text-slate-400">{index + 1}</span>
                }
              </div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getAvatarBg(agency.company)} ${index < 3 ? MEDAL_RINGS[index] : ''}`}>
                <span className="text-xs font-bold text-white tracking-wide">
                  {getAgencyInitials(agency.name)}
                </span>
              </div>
            </div>
          </td>
        );
      case 'name':
        return (
          <td key="name" className="px-4 py-4 whitespace-nowrap">
            <div className="flex flex-col">
              <span className="text-sm font-bold text-slate-800">{agency.name}</span>
              <span className="text-xs text-indigo-500 mt-0.5 font-medium">Voir le détail →</span>
            </div>
          </td>
        );
      case 'company':
        return (
          <td key="company" className="px-4 py-4 whitespace-nowrap">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getCompanyBadgeClass(agency.company)}`}>
              {agency.company}
            </span>
          </td>
        );
      case 'objective':
        return (
          <td key="objective" className="px-4 py-4 whitespace-nowrap">
            {moneyCell(agency.annual_objective, 'border-slate-300 text-slate-600 bg-slate-50')}
          </td>
        );
      case 'potential':
        return (
          <td key="potential" className="px-4 py-4 whitespace-nowrap">
            {showPotential
              ? moneyCell((agency as any).potential_sales || 0, 'border-amber-300 text-amber-700 bg-amber-50')
              : <span className="text-slate-300 text-sm">—</span>}
          </td>
        );
      case 'sales':
        return (
          <td key="sales" className="px-4 py-4 whitespace-nowrap">
            {moneyCell(agency.total_sales, 'border-emerald-300 text-emerald-700 bg-emerald-50')}
          </td>
        );
      case 'cumulative': {
        const cumul = showPotential
          ? ((agency as any).potential_sales || 0) + agency.total_sales
          : agency.total_sales;
        return (
          <td key="cumulative" className="px-4 py-4 whitespace-nowrap">
            {moneyCell(cumul, 'border-indigo-300 text-indigo-700 bg-indigo-50')}
          </td>
        );
      }
      case 'remaining':
        return (
          <td key="remaining" className="px-4 py-4 whitespace-nowrap">
            {agency.annual_objective ? (
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                agency.remaining > 0
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
              }`}>
                <span>{agency.remaining > 0 ? '↘' : '↗'}</span>
                {formatCurrency(Math.abs(agency.remaining))}
              </span>
            ) : <span className="text-slate-300 text-sm">—</span>}
          </td>
        );
      case 'completion':
        return (
          <td key="completion" className="px-4 py-4 whitespace-nowrap">
            {agency.annual_objective ? (
              <div className="flex flex-col gap-1.5">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border w-fit ${
                  agency.completion_rate >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  agency.completion_rate >= 75  ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  agency.completion_rate >= 50  ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                   'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {agency.completion_rate.toFixed(1)}%
                </span>
                <div className="h-1 w-20 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      agency.completion_rate >= 100 ? 'bg-emerald-400' :
                      agency.completion_rate >= 75  ? 'bg-blue-400' :
                      agency.completion_rate >= 50  ? 'bg-amber-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${Math.min(agency.completion_rate, 100)}%` }}
                  />
                </div>
              </div>
            ) : <span className="text-slate-300 text-sm">—</span>}
          </td>
        );
      case 'negotiators':
        return (
          <td key="negotiators" className="px-4 py-4 whitespace-nowrap">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 text-xs font-bold text-indigo-700 border border-indigo-200">
              {agency.negotiator_count}
            </span>
          </td>
        );
      case 'transactions':
        return (
          <td key="transactions" className="px-4 py-4 whitespace-nowrap">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-xs font-bold text-slate-600 border border-slate-200">
              {agency.sales_count}
            </span>
          </td>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* ── Action bar ── */}
      <div className="relative mb-4" ref={columnPanelRef}>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3">
            <label className="text-xs font-medium text-slate-500 shrink-0">Type de classement</label>
            <select
              value={classementMode}
              onChange={e => setClassementMode(e.target.value)}
              className="max-w-xs px-3 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
            >
              <option value="global">Classement global (toutes agences)</option>
              <option value="par-entite">Classement par entité</option>
              {COMPANY_GROUPS.map(group => (
                <option key={group.key} value={group.key}>{group.label}</option>
              ))}
            </select>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={handleExportExcel}
                disabled={exporting}
                title="Exporter Excel"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <FileDown size={15} />
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                title="Exporter PDF"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <FileText size={15} />
              </button>
              <div className="w-9" />
            </div>
          </div>
        </div>

        {/* Colonnes button — outside overflow-hidden */}
        <div className="absolute right-4 bottom-3 z-10">
          <button
            onClick={() => setShowColumnPanel(p => !p)}
            title="Gérer les colonnes"
            className={`relative inline-flex items-center justify-center w-8 h-8 rounded-xl border transition-colors ${
              showColumnPanel
                ? 'bg-[#2d3a5e] text-white border-[#2d3a5e]'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={15} />
            {hiddenCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-500 text-white text-[9px] font-bold">
                {hiddenCount}
              </span>
            )}
          </button>
          {showColumnPanel && (
            <div className="absolute right-0 top-full mt-2 z-50">
              <ColumnConfigPanel
                columns={columns}
                onColumnsChange={setColumns}
                defaultColumns={DEFAULT_COLUMNS}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden agency-table-section">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {visibleColumns.map(col => renderHeader(col))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {classementMode === 'par-entite' ? (
                // ── Groupé par entité avec séparateurs ──
                COMPANY_GROUPS.map(group => {
                  const groupAgencies = sortedAgencies.filter(a => a.company === group.key);
                  if (groupAgencies.length === 0) return null;
                  const groupHP = groupAgencies.reduce((s, a) => s + (a.potential_sales ?? 0), 0);
                  const groupHR = groupAgencies.reduce((s, a) => s + a.total_sales, 0);
                  return (
                    <React.Fragment key={group.key}>
                      <tr className={`${group.bg} border-b-2 ${group.border}`}>
                        <td colSpan={visibleColumns.length} className="px-5 py-2">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${group.dot}`} />
                            <span className={`text-xs font-bold uppercase tracking-wider ${group.text}`}>
                              {group.label}
                            </span>
                            <span className="text-[11px] text-slate-400 font-medium">
                              {groupAgencies.length} agence{groupAgencies.length > 1 ? 's' : ''}
                            </span>
                            {showPotential && <span className={`text-xs font-medium ${group.text} opacity-60 ml-2`}>HP : {fmtK(groupHP)} ·</span>}
                            <span className={`text-xs font-medium ${group.text} opacity-60 ${showPotential ? '' : 'ml-2'}`}>HR : {fmtK(groupHR)}</span>
                          </div>
                        </td>
                      </tr>
                      {groupAgencies.map((agency, index) => (
                        <tr key={agency.name} className="hover:bg-indigo-50/30 cursor-pointer transition-colors border-b border-slate-100 last:border-0" onClick={() => setSelectedAgency(agency)}>
                          {visibleColumns.map(col => renderCell(col, agency, index))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              ) : (
                // ── Global ou entité filtrée : classement simple ──
                sortedAgencies.map((agency, index) => (
                  <tr key={agency.name} className="hover:bg-indigo-50/30 cursor-pointer transition-colors border-b border-slate-100 last:border-0" onClick={() => setSelectedAgency(agency)}>
                    {visibleColumns.map(col => renderCell(col, agency, index))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AgencyDetailsModal
        isOpen={selectedAgency !== null}
        onClose={() => setSelectedAgency(null)}
        agency={selectedAgency}
        startDate={startDate}
        endDate={endDate}
        period={period}
      />
    </>
  );
}
